import { useState } from 'react';
import { MODEL_CATEGORIES, MODELS_3D } from '@/data/content';

export default function ModelsPage() {
  const [activeFilter, setActiveFilter] = useState('最新');
  const filtered = activeFilter === '最新' || activeFilter === '最热'
    ? MODELS_3D
    : MODELS_3D.filter((m) => m.category === activeFilter);

  return (
    <div className="max-w-6xl mx-auto px-6 pt-8 pb-12">
      {/* ---- Header ---- */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-extrabold text-text-primary mb-2">创造者社区</h1>
          <p className="text-sm text-text-secondary">下载模型，打印出来，装上车。被采纳即获 15%-20% 佣金。结构件和程序双赛道开放。</p>
        </div>
        <button className="px-4 h-9 rounded-btn border border-arena-blue text-arena-blue text-sm hover:bg-arena-blue/10 transition-all shrink-0">上传模型</button>
      </div>

      {/* ---- Filter + Stats ---- */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div className="flex gap-1.5 flex-wrap">
          {MODEL_CATEGORIES.map((cat) => (
            <button key={cat} onClick={() => setActiveFilter(cat)} className={`px-3 h-7 rounded-btn text-xs font-medium transition-colors ${activeFilter === cat ? 'bg-arena-blue text-white' : 'bg-border-dark text-text-secondary hover:text-text-primary'}`}>{cat}</button>
          ))}
        </div>
        <div className="flex gap-4 text-xs text-text-disabled">
          <span>{filtered.length} 个模型</span>
          <span className="text-text-secondary">3,456 总下载</span>
        </div>
      </div>

      {/* ---- Masonry Grid ---- */}
      <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4 mb-10">
        {filtered.map((m) => (
          <div key={m.id} className="break-inside-avoid bg-panel-dark/40 border border-border-dark rounded-card mecha-card mecha-bolt overflow-hidden hover:border-arena-blue/40 transition-all group cursor-pointer">
            <div className="relative">
              <img src={m.image} alt={m.name} className="w-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-bg-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                <span className="text-[11px] text-text-primary">点击预览 3D 模型</span>
              </div>
            </div>
            <div className="p-3">
              <h3 className="text-xs font-medium text-text-primary mb-2">{m.name}</h3>
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-4 h-4 rounded-full bg-border-dark shrink-0" />
                <span className="text-[11px] text-text-secondary">{m.creator}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-text-secondary">{m.downloads.toLocaleString()} 下载</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-tag ${m.commission === 25 ? 'bg-arena-gold/15 text-arena-gold' : 'bg-border-dark text-arena-gold'}`}>{m.commission}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mb-14">
        <button className="px-5 h-9 rounded-btn bg-border-dark text-text-secondary text-sm hover:text-text-primary hover:border-arena-blue/40 border border-transparent transition-all">加载更多</button>
      </div>

      {/* ---- How It Works ---- */}
      <h2 className="text-lg font-extrabold text-text-primary mb-4">如何参与</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
        {[
          { step: '01', t: '设计建模', d: '用 Fusion 360 或任何 CAD 软件设计结构件。确保与标准底盘安装孔位兼容。输出 STL/STEP 格式。' },
          { step: '02', t: '打印测试', d: '自己先打印出来装车实测。至少经过 3 场对抗赛的实战检验。拍照或录视频作为审核材料。' },
          { step: '03', t: '上传提交', d: '在模型库上传 STL 文件 + 实拍照片 + 安装说明 + 推荐打印参数（层高/填充率/支撑）。' },
          { step: '04', t: '上架销售', d: '官方 3 个工作日内审核。通过后自动上架，每笔订单按比例结算佣金，月结到账。' },
        ].map((s) => (
          <div key={s.step} className="bg-panel-dark/40 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-5">
            <span className="text-2xl font-extrabold font-mono text-arena-blue/70">{s.step}</span>
            <h3 className="text-sm font-semibold text-text-primary mt-3 mb-2">{s.t}</h3>
            <p className="text-xs text-text-secondary leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>

      {/* ---- Program Track ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-panel-dark/40 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-6">
          <h3 className="text-sm font-semibold text-arena-gold mb-3">结构件赛道</h3>
          <p className="text-xs text-text-secondary mb-4 leading-relaxed">推铲、侧甲、支架、外观壳、工具件——任何可以装上车并提升性能或外观的物理零件。材料不限（PLA/ABS/PETG/TPU），须标注推荐打印参数。</p>
          <div className="flex flex-wrap gap-1">
            {['STL', 'STEP', '3MF', 'Fusion 360', 'Onshape'].map((t) => (
              <span key={t} className="text-[10px] bg-border-dark text-text-disabled px-1.5 py-0.5 rounded-tag">{t}</span>
            ))}
          </div>
        </div>
        <div className="bg-panel-dark/40 border border-border-dark rounded-card-lg mecha-card mecha-bolt p-6">
          <h3 className="text-sm font-semibold text-arena-blue mb-3">程序赛道</h3>
          <p className="text-xs text-text-secondary mb-4 leading-relaxed">PID 调参文件、自主对抗策略、传感器融合算法、自定义固件模块——代码即战力。提交源码 + 对战视频验证。</p>
          <div className="flex flex-wrap gap-1">
            {['Python', 'C++', 'MicroPython', 'JSON配置', '固件MOD'].map((t) => (
              <span key={t} className="text-[10px] bg-border-dark text-text-disabled px-1.5 py-0.5 rounded-tag">{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
